// The role rules, mirrored from the backend.
//
// portal/security/rbac.py is the authority — nothing here is a decision, it is
// a copy kept so the UI can grey out what the server would refuse anyway. Keep
// the two in step; when they disagree the server wins and the user sees a 403
// from a control that looked enabled.
//
// Ranks run lowest-is-strongest, exactly as in rbac.py.
export const ROLE_ORDER = ["admin", "group_admin", "operator", "user"];
export const ROLE_RANK = { admin: 0, group_admin: 1, operator: 2, user: 3 };
export const ROLE_LABELS = {
  admin: "Admin",
  group_admin: "Group admin",
  operator: "Operator",
  user: "User",
};
export const ROLE_COLORS = {
  admin: "amber",
  group_admin: "purple",
  operator: "blue",
  user: "gray",
};

// Every grant the account holds. Older responses carry only `role`, so fall
// back to it rather than rendering an account as having no roles at all.
export function grantsOf(user) {
  if (!user) return [];
  if (Array.isArray(user.roles) && user.roles.length) return user.roles;
  return user.role ? [{ role: user.role, group_id: user.group_id ?? null }] : [];
}

export function isGlobalAdmin(user) {
  return grantsOf(user).some(g => g.role === "admin");
}

// Strongest rank held anywhere. For deciding what to SHOW; never for deciding
// what may be done to a particular group's object — see rankInGroup.
export function topRank(user) {
  const ranks = grantsOf(user).map(g => ROLE_RANK[g.role]).filter(r => r !== undefined);
  return ranks.length ? Math.min(...ranks) : null;
}

export function rankInGroup(user, groupId) {
  if (isGlobalAdmin(user)) return ROLE_RANK.admin;
  if (groupId === null || groupId === undefined) return null;
  const ranks = grantsOf(user)
    .filter(g => g.group_id === groupId)
    .map(g => ROLE_RANK[g.role])
    .filter(r => r !== undefined);
  return ranks.length ? Math.min(...ranks) : null;
}

// Holds at least `role` somewhere. The coarse test the nav and page guards use.
export function hasAtLeast(user, role) {
  const rank = topRank(user);
  return rank !== null && rank <= ROLE_RANK[role];
}

// Groups where the account manages anything, for the pickers.
export function managedGroupIds(user, minRole = "operator") {
  return grantsOf(user)
    .filter(g => g.group_id != null && ROLE_RANK[g.role] <= ROLE_RANK[minRole])
    .map(g => g.group_id);
}

// The same groups, with their names, for a picker that has to render them.
// Split from managedGroupIds rather than replacing it because most callers only
// want the ids and `group_name` is not always present on a grant.
export function managedGroups(user, minRole = "operator") {
  return grantsOf(user)
    .filter(g => g.group_id != null && ROLE_RANK[g.role] <= ROLE_RANK[minRole])
    .map(g => ({ id: g.group_id, name: g.group_name || `Group ${g.group_id}` }));
}

// Holds one of these roles, in any group.
//
// **Deliberately not a rank question**, which is why hasAtLeast does not answer
// it: "is this person facility staff" is asking which hat they wear, and
// `hasAtLeast(u, "operator")` is true for an admin, who wears a different one.
// The two are easy to confuse and the difference is the whole reason this lives
// here rather than being written out at a call site.
export function holdsAny(user, roles) {
  return grantsOf(user).some(g => roles.includes(g.role));
}

// Which roles this account may hand out in `groupId`. Mirrors can_grant: any
// role strictly below your own rank there, with admin as the one exception.
export function grantableRoles(user, groupId) {
  if (isGlobalAdmin(user)) return ROLE_ORDER;
  const mine = rankInGroup(user, groupId);
  if (mine === null) return [];
  return ROLE_ORDER.filter(r => r !== "admin" && ROLE_RANK[r] > mine);
}

// Every role this account could grant somewhere — for a role picker offered
// BEFORE a group is chosen. `admin` is unscoped by definition, so it appears
// only for a global admin and never asks which group.
export function grantableAnywhere(user) {
  if (isGlobalAdmin(user)) return ROLE_ORDER;
  const best = topRank(user);
  if (best === null) return [];
  return ROLE_ORDER.filter(r => r !== "admin" && ROLE_RANK[r] > best);
}

// Does granting this role require naming a group?
//
// `admin` and `user` are the unscoped two, for opposite reasons: an admin
// already sees every group, so a group on the grant means nothing and the
// server forces it to NULL; a plain user belongs to no group at all and
// reaches a project by owning it or being assigned to it.
//
// Lifted out of RoleEditor when the role picker became a checkbox per role —
// it is now asked once per row rather than once per form, and the mirror of
// rbac.py belongs in this file rather than in a component.
export function roleNeedsGroup(role) {
  return role !== "admin" && role !== "user";
}

// Groups in which `role` may actually be granted. Empty for `admin`, which is
// not a group role at all.
export function groupsForRole(user, role, groups) {
  if (role === "admin") return [];
  return groups.filter(g => grantableRoles(user, g.id).includes(role));
}

// Whether this grant's holder is reported on the whole group's work rather than
// only their own — the same split the backend's rbac.sees_whole_group makes.
//
// Here rather than inline at the call site for the reason the ratchet exists:
// five checks written before `group_admin` existed put it in the plain-user
// branch, and each read like a harmless comparison to a literal.
export function grantSeesWholeGroup(role) {
  return ROLE_RANK[role] <= ROLE_RANK.group_admin;
}

/**
 * Whether staff have vouched for this account (2026-09-28). A self-registered
 * account is `incomplete` until the person submits their details and
 * `submitted` until an operator confirms them; the server refuses it
 * everything but its own profile meanwhile (`deps._assert_profile_confirmed`).
 * An object with no `profile_state` at all is an older `/auth/me` answer or a
 * test double, and is taken as confirmed, as the server's column default is.
 */
export function profileConfirmed(user) {
  return !user?.profile_state || user.profile_state === "confirmed";
}

/**
 * Whether the account is a plain researcher: its strongest role (the cached
 * `role`) is `user`. Such an account holds no group since 2026-09-28 — what
 * places it is the projects it owns, is assigned to or represents
 * (`UserOut.projects`) — so a screen shows those where it shows staff a group.
 */
export function isResearcher(user) {
  return user?.role === "user";
}

/**
 * Whether an account in `role` has a billing identity of its own — `user`
 * only. Mirrors `accounts.billed_as_self`: an operator or group admin carries
 * their group's, read live, and an administrator none. A missing role reads as
 * `user`, as it does on the server.
 */
export function billedAsSelf(role) {
  return (ROLE_RANK[role] ?? ROLE_RANK.user) > ROLE_RANK.operator;
}
